import { useEffect, useRef, useState } from 'react';
import type { PostHog } from 'posthog-js';
import { z } from 'zod';
import { capturePostHogEvent } from '../../utils/analytics';
import { safeGetStorageItem, safeSetStorageItem } from '../../utils/browserStorage';
import { watchExperimentFlags } from '../../utils/experimentFlagUpdates';
import {
  getChallengeExperimentEventProperties,
  type ChallengeExperimentContext,
} from './challengePageExperiment';

const getPostHogClient = (): PostHog | undefined => {
  // SAFETY: analytics initialization publishes its SDK on window; optional
  // access preserves the pre-initialization state without creating a client.
  return (window as Window & { posthog?: PostHog }).posthog;
};
const versionSchema = z.union([z.string().min(1).max(128), z.number().finite()]);
const receiptSchema = z.object({
  scope: z.string(),
  flagCalled: z.boolean(),
  exposure: z.boolean(),
  flagId: z.string().uuid(),
  exposureId: z.string().uuid(),
  timestamp: z.string().datetime(),
});

/** Provider metadata is optional; only a bounded scalar belongs in attribution. */
export const getChallengeExperimentVersion = (flag: string): string | number | undefined => {
  const version =
    getPostHogClient()?.persistence?.props?.$feature_flag_details?.[flag]?.metadata?.version;
  const parsed = versionSchema.safeParse(version);
  return parsed.success ? parsed.data : undefined;
};

type ExposureReceipt = z.infer<typeof receiptSchema>;
// Keep the same receipt through SPA remounts when browser storage is blocked.
const receipts = new Map<string, ExposureReceipt>();

/**
 * The challenge owns exposure after its deciding design commits. Assignment
 * cache is not delivery evidence. Retry only unacknowledged SDK handoffs and
 * retain stable insert IDs so a provider retry cannot manufacture enrollment.
 */
export default function useChallengeExperimentExposure(context?: ChallengeExperimentContext): void {
  const latestContext = useRef(context);
  latestContext.current = context;
  const [, setMetadataRevision] = useState(0);
  const contextKey = JSON.stringify(context);

  useEffect(() => {
    const capture = () => {
      const current = latestContext.current;
      const client = getPostHogClient();
      if (!current?.is_experiment_active || !client) return;
      const identity = client.get_distinct_id?.();
      const session = client.get_session_id?.() || 'current-browser-session';
      if (!identity) return;
      const flag = current.experiment_flag;
      const arm = current.experiment_variant;
      const scope = JSON.stringify([
        flag,
        identity,
        session,
        current.experiment_version ?? null,
        arm,
      ]);
      const storageKey = `botspot:challenge:exposure:${encodeURIComponent(scope)}`;
      let receipt = receipts.get(scope);
      if (!receipt) {
        try {
          const stored = receiptSchema.safeParse(
            JSON.parse(safeGetStorageItem('sessionStorage', storageKey) || 'null'),
          );
          receipt = stored.success && stored.data.scope === scope ? stored.data : undefined;
        } catch {
          /* An unavailable receipt never suppresses a real exposure. */
        }
        receipt ||= {
          scope,
          flagCalled: false,
          exposure: false,
          flagId: crypto.randomUUID(),
          exposureId: crypto.randomUUID(),
          timestamp: new Date().toISOString(),
        };
        receipts.set(scope, receipt);
      }
      const properties = {
        ...getChallengeExperimentEventProperties(current),
        page: window.location.pathname,
        challenge_path: window.location.pathname,
        surface: 'challenge_landing_page',
        variant: arm,
        feature_flag_value: arm,
        $feature_flag: flag,
        $feature_flag_response: arm,
        ...(current.experiment_version !== undefined && {
          $feature_flag_version: current.experiment_version,
        }),
      };
      for (const [event, key] of [
        ['$feature_flag_called', 'flagCalled'],
        ['experiment_exposure', 'exposure'],
      ] as const) {
        if (receipt[key]) continue;
        const uuid = key === 'flagCalled' ? receipt.flagId : receipt.exposureId;
        receipt[key] =
          capturePostHogEvent(
            event,
            {
              ...properties,
              $insert_id: uuid,
              $timestamp: receipt.timestamp,
            },
            { uuid, timestamp: new Date(receipt.timestamp) },
          ) === true;
        safeSetStorageItem('sessionStorage', storageKey, JSON.stringify(receipt));
      }
    };
    // Effects run after the deciding arm's DOM is committed. Flag refreshes
    // also retry failed handoffs and notice identity/session changes.
    capture();
    return watchExperimentFlags(getPostHogClient(), () => {
      capture();
      setMetadataRevision((revision) => revision + 1);
    });
  }, [contextKey]);
}
